// Using defineSchema from core instead of importing `schema` from @json-render/react
// because this is a shared lib also used by backend.
import { defineCatalog, defineSchema } from '@json-render/core';
import { z } from 'zod';

import { blockSchema } from '@eluve/llm-outputs';

import {
  expandableAnalyticsSchema,
  linkAnalyticsSchema,
  specSchema,
} from './sdui-types';

export type { Spec } from '@json-render/core';

const schema = defineSchema((s) => ({
  spec: s.object({
    root: s.string(),
    elements: s.record(
      s.object({
        type: s.ref('catalog.components'),
        props: s.propsOf('catalog.components'),
        children: s.array(s.string()),
        visible: s.any(),
      }),
    ),
  }),
  catalog: s.object({
    components: s.map({
      props: s.zod(),
      slots: s.array(s.string()),
      description: s.string(),
      example: s.any(),
    }),
    actions: s.map({
      params: s.zod(),
      description: s.string(),
    }),
  }),
}));

const linkActionPropsSchema = z.union([
  z.object({
    type: z.literal('popover'),
    trigger: z.enum(['hover', 'click']).optional(),
  }),
  z.object({
    type: z.literal('dialog'),
    title: z.string(),
    subtitle: z.string().optional(),
  }),
  z.object({
    type: z.literal('navigate'),
    url: z.string(),
    target: z.enum(['_self', '_blank']).optional(),
  }),
]);

const catalogComponents = {
  Root: {
    props: z.object({}),
    slots: ['default'],
    description: 'Root container that renders children',
  },
  Text: {
    props: z.object({
      content: z.string(),
      variant: z
        .enum(['heading', 'subheading', 'body', 'label', 'caption'])
        .optional(),
      markdown: z.boolean().optional(),
      size: z.enum(['3xl', '2xl', 'xl', 'lg', 'md', 'sm']).optional(),
      weight: z.enum(['bold', 'semibold', 'medium', 'regular']).optional(),
      color: z
        .enum([
          'primary',
          'secondary',
          'tertiary',
          'supporting',
          'disabled',
          'inherit',
        ])
        .optional(),
    }),
    description:
      'Styled text with variant-based rendering. Optional size/weight/color override the variant defaults.',
  },
  Divider: {
    props: z.object({}),
    description: 'Visual separator line',
  },
  Expandable: {
    props: z.object({
      title: z.string(),
      content: z.string().optional(),
      titleLinksSpec: specSchema.optional(),
      defaultOpen: z.boolean().optional(),
      analytics: expandableAnalyticsSchema.optional(),
      feedbackMetadata: z.record(z.unknown()).optional(),
    }),
    slots: ['default'],
    description: 'Collapsible section with title, analytics, and feedback',
  },
  Link: {
    props: z.object({
      text: z.string(),
      style: z
        .enum(['citation', 'citation-source', 'inline', 'button', 'source'])
        .optional(),
      citationNumber: z.number().optional(),
      action: linkActionPropsSchema,
      analytics: linkAnalyticsSchema.optional(),
      asSpan: z.boolean().optional(),
    }),
    slots: ['default'],
    description: 'Interactive link with popover, dialog, or navigation action',
  },
  DynamicArtifact: {
    props: z.object({
      blocks: z.array(blockSchema),
    }),
    description: 'Renders structured data blocks (SOAP notes, etc.)',
  },
  Alert: {
    props: z.object({
      variant: z.enum(['error', 'warning', 'info']),
      icon: z.string().optional(),
      title: z.string().optional(),
      message: z.string().optional(),
      // `titleKey` and `messageKey` carry `@eluve/i18n` message keys that the
      // `AlertRenderer` resolves at render time. Prefer these over literal
      // `title`/`message` for any recipe whose strings should be localized;
      // when both are set, keys win. Other recipes can keep emitting plain
      // strings for backward compatibility.
      titleKey: z.string().optional(),
      messageKey: z.string().optional(),
      filled: z.boolean().optional(),
    }),
    description:
      'Status alert with optional icon. Provide either literal `title`/`message` or `titleKey`/`messageKey` (i18n keys resolved by the renderer).',
  },
  Icon: {
    props: z.object({
      name: z.string(),
      size: z.enum(['xs', 'sm', 'md', 'lg']).optional(),
    }),
    description: 'Lucide icon by name',
  },
  Button: {
    props: z.object({
      text: z.string(),
      variant: z.enum(['brand', 'default']).optional(),
      size: z.enum(['s', 'm', 'l']).optional(),
      disabled: z.boolean().optional(),
      loading: z.boolean().optional(),
      wFull: z.boolean().optional(),
    }),
    description: 'Interactive button that emits a "press" event',
  },
  Image: {
    props: z.object({
      src: z.string(),
      alt: z.string(),
      width: z.enum(['full', 'half', 'third']).optional(),
      rounded: z.enum(['sm', 'md', 'lg', 'xl']).optional(),
    }),
    description: 'Displays an image',
  },
  Box: {
    props: z.object({
      direction: z.enum(['row', 'column']).optional(),
      gap: z.number().optional(),
      align: z.enum(['start', 'center', 'end', 'stretch']).optional(),
      justify: z.enum(['start', 'center', 'end', 'between']).optional(),
      padding: z.number().optional(),
      fullWidth: z.boolean().optional(),
      textAlign: z.enum(['left', 'center', 'right']).optional(),
    }),
    slots: ['default'],
    description:
      'Layout container with semantic props for direction, gap, alignment, and padding',
  },
  Card: {
    props: z.object({
      variant: z.enum(['default', 'subtle']).optional(),
      padding: z.number().optional(),
      rounded: z.enum(['lg', 'xl']).optional(),
      direction: z.enum(['row', 'column']).optional(),
      gap: z.number().optional(),
      align: z.enum(['start', 'center', 'end', 'stretch']).optional(),
    }),
    slots: ['default'],
    description:
      'Styled content card. "subtle" variant uses tertiary background. Supports layout props.',
  },
  InstructionStep: {
    props: z.object({
      step: z.number(),
      title: z.string(),
      imageSrc: z.string(),
      imageAlt: z.string(),
    }),
    description:
      'Numbered instruction step card with text on the left and a bordered image on the right.',
  },
  AlertBanner: {
    props: z.object({
      variant: z.enum(['error', 'success', 'info', 'warning']),
    }),
    slots: ['default'],
    description:
      'Inline alert banner with colored background. Children are rendered inside.',
  },
  Progress: {
    props: z.object({
      value: z.number(),
      variant: z.enum(['default', 'success']).optional(),
    }),
    description: 'Progress bar with a value from 0 to 100',
  },
  Chart: {
    props: z.object({
      chartType: z.enum(['bar', 'line', 'area', 'pie']),
      data: z.array(z.record(z.union([z.string(), z.number()]))),
      dataKeys: z.array(z.string()),
      nameKey: z.string().optional(),
      title: z.string().optional(),
      description: z.string().optional(),
      config: z
        .record(
          z.object({
            label: z.string(),
            color: z.string().optional(),
          }),
        )
        .optional(),
      xAxisKey: z.string().optional(),
      stacked: z.boolean().optional(),
    }),
    description:
      'Data visualization chart supporting bar, line, area, and pie types. Include all data directly in the element props - do not use $state for chart data.',
    example: {
      chartType: 'bar',
      data: [
        { month: 'Jan', revenue: 1200, expenses: 900 },
        { month: 'Feb', revenue: 1800, expenses: 1100 },
      ],
      dataKeys: ['revenue', 'expenses'],
      xAxisKey: 'month',
      title: 'Monthly Financials',
      config: {
        revenue: { label: 'Revenue', color: '#007AFF' },
        expenses: { label: 'Expenses', color: '#FF3B30' },
      },
    },
  },
};

export const eluveCatalog = defineCatalog(schema, {
  components: catalogComponents,
  actions: {},
});

/** Props map derived from the catalog — keyed by component name. */
export type CatalogPropsMap = {
  [K in keyof typeof catalogComponents]: z.infer<
    (typeof catalogComponents)[K]['props']
  >;
};
